// Lightweight retrieval over the Intel ISEF project database (public/data).
// Used to ground document generation in REAL competition entries instead of
// letting the model fabricate project references. Self-contained and fail-safe:
// any error returns [] so document generation continues normally.
import * as XLSX from 'xlsx';

let _cache = null;
let _loading = null;

async function loadISEF() {
  if (_cache) return _cache;
  if (_loading) return _loading;
  _loading = (async () => {
    const res = await fetch('/data/ISEF_International_Projects.xlsx');
    if (!res.ok) throw new Error(`ISEF data load failed: ${res.status}`);
    const buf = await res.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    _cache = XLSX.utils.sheet_to_json(sheet);
    return _cache;
  })();
  return _loading;
}

const STOP = new Set([
  'the', 'a', 'an', 'of', 'and', 'or', 'for', 'to', 'in', 'on', 'with', 'using',
  'effect', 'effects', 'study', 'experiment', 'experiments', 'analysis', 'how',
  'does', 'do', 'vs', 'via', 'by', 'from', 'into', 'measuring', 'building',
  'determining', 'investigating', 'circuit', 'circuits',
]);

function tokenize(s) {
  return (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length > 2 && !STOP.has(t));
}

/**
 * Return the top-K real ISEF projects most relevant to a topic.
 * @returns {Promise<Array<{title:string, category:string, awards:string}>>}
 */
export async function getRelevantISEFProjects(topic, k = 8) {
  let rows;
  try {
    rows = await loadISEF();
  } catch (e) {
    console.warn('ISEF retrieval skipped:', e.message);
    return [];
  }

  const terms = tokenize(topic);
  if (!terms.length) return [];

  const scored = [];
  for (const r of rows) {
    const title = (r['Project Title'] || r.title || '').toString();
    if (!title) continue;
    const category = (r['Category'] || r.category || '').toString();
    const awards = (r['Awards Won'] || r.awards || '').toString();
    const titleL = title.toLowerCase();
    const catL = category.toLowerCase();

    let score = 0;
    for (const t of terms) {
      const re = new RegExp(`\\b${t}\\b`);
      if (re.test(titleL)) score += 5;
      if (re.test(catL)) score += 2;
    }
    if (score > 0) scored.push({ score, title, category, awards });
  }

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, k).map(({ title, category, awards }) => ({ title, category, awards }));
}
